/* global window */
import 'rxjs'
import intl from 'react-intl-universal'
import { createLogger } from 'redux-logger'
import wireupAngular from './dispatchAngular'
import thunk from 'redux-thunk'
import rootReducerCombiner from './reducers'
import angular from 'angular'
import ngRedux from 'ng-redux'
import { createEpicMiddleware } from 'redux-observable'
import rootEpic from './epics'
import { processSideEffects } from 'redux-action-side-effects'
import { bounceSideEffectsStoreEnhancer } from './reduxSideEffects'
import { initAction } from './commonActions'
import checkConnectivity from './connectivity'

const serviceResolver = (service) => serviceResolver.injector.get(service)

export const _setServiceResolverInjector = (injector) => {
  serviceResolver.injector = injector
}

export const featureFlag = (key) =>
  featureFlag.featuresResolver().isEnabled(key)

export const _setFeatureFlagResolver = ($rootScope) => {
  featureFlag.featuresResolver = () => $rootScope.features
}

export const _epicDependencies = {
  serviceResolver,
  html5RedirectTo: (path) => {
    window.location.href = path
  },
  showSuccess: (message) => {
    // When overriding default title and configs, object passed rather than string
    const noOverrides = typeof message === 'string'
    if (noOverrides) {
      serviceResolver('toastr').success(message, intl.get('common.success'))
    } else {
      serviceResolver('toastr').success(
        message.messageBody,
        message.titleOverride,
        message.options || {}
      )
    }
  },
  showError: (error) => {
    if (error.toastConfig) {
      const {
        message,
        title = intl.get('common.error'),
        optionsOverride = {},
      } = error.toastConfig
      serviceResolver('toastr').error(message, title, optionsOverride)
    } else {
      serviceResolver('toastr').error(error.message, intl.get('common.error'))
    }
  },
  showInfo: (info) => {
    if (info.toastConfig) {
      const { message, title = '', optionsOverride = {} } = info.toastConfig
      serviceResolver('toastr').info(message, title, optionsOverride)
    } else {
      serviceResolver('toastr').info(info.message, '')
    }
  },
  clearToast: () => {
    serviceResolver('toastr').clear()
  },
  closeTab: () => {
    // Close tab or redirect to the root if unable to close tab
    window.onbeforeunload = null
    setTimeout(() => _epicDependencies.html5RedirectTo(''), 500)
    window.close()
  },
  featureFlag,
}

export function configureRedux($ngReduxProvider) {
  // Integrate Redux Dev Tools browser extension for dev mode
  const reduxDevTools =
    window.__REDUX_DEVTOOLS_EXTENSION__ && window.__REDUX_DEVTOOLS_EXTENSION__()

  const devMode = process.env.NODE_ENV === 'testing'

  const epicMiddleware = createEpicMiddleware(rootEpic, {
    dependencies: _epicDependencies,
  })

  // store enhancers. Allows empty/null elements
  // Adds redux dev tools for dev builds and get rid of it in prod
  const storeEnhancers = [
    devMode ? reduxDevTools : null,
    processSideEffects,
    bounceSideEffectsStoreEnhancer,
  ]

  // redux middleware. Allows empty/null elements
  // Add logger for dev mode, remove it for prods
  const middleware = [
    thunk,
    devMode
      ? createLogger({
          duration: true,
          collapsed: true,
        })
      : null,
    epicMiddleware,
  ]

  // Initialize redux using ngReduxProvider
  $ngReduxProvider.createStoreWith(
    rootReducerCombiner(),
    middleware.filter(angular.identity),
    storeEnhancers.filter(angular.identity)
  )
}

export function reduxStartup(
  $ngRedux,
  $rootScope,
  storyService,
  $injector,
  $window
) {
  _setServiceResolverInjector($injector)
  _setFeatureFlagResolver($rootScope)

  bounceSideEffectsStoreEnhancer.store = $ngRedux

  // Dispatch INIT action so reducers can set up their initial state
  $ngRedux.dispatch(initAction())

  // Wire up Angular broadcasts to redux
  wireupAngular($ngRedux, $rootScope)

  // Enable connectivity check
  checkConnectivity($window, $ngRedux)
}

angular
  .module('ellipsis.redux', [ngRedux])
  .config(['$ngReduxProvider', configureRedux])
  .run([
    '$ngRedux',
    '$rootScope',
    'storyService',
    '$injector',
    '$window',
    reduxStartup,
  ])
